/**
 * Analytics Portal V1 — the Capital metric map (Phase 13, plan §4.5).
 *
 * Only keys the Phase 8 `GET /api/analytics/capital` payload contains, and never a value this file
 * computes. Capital-directory discovery lives in its own card (`CapitalDirectoryCard`) because the
 * server sends it as a dedicated block with its own zero-state contract, so the Discovery group here
 * covers search only and nothing is reported twice. `search_ctr` is present in the payload but
 * deliberately never rendered in V1 (plan §12.12 A11-2), and `pitch_decks` is rendered by
 * `PitchDeckSection` from the richer `pitch_decks` block instead of as a bare counter.
 */
export const CAPITAL_HEADLINE = [
  { key: "visits", label: "Capital profile visits", hint: "One visit per session." },
  { key: "unique_visitors", label: "Unique visitors" },
  { key: "returning_visitors", label: "Returning visitors" },
  { key: "engagement_rate", label: "Engagement rate", suffix: "%" },
];

export const CAPITAL_GROUPS = [
  {
    id: "discovery",
    title: "Search discovery",
    description: "Startup Otter search impressions and clicks for this Capital profile.",
    metrics: [
      { key: "search_impressions", label: "Search impressions" },
      { key: "search_clicks", label: "Search clicks" },
      { key: "unique_search_clickers", label: "Unique search clickers" },
    ],
  },
  {
    id: "followers",
    title: "Followers, fans & bookmarks",
    description: "Current totals are live; losses and net change are measured from instrumentation.",
    metrics: [
      { key: "followers_current", label: "Followers now" },
      { key: "followers_added", label: "Followers gained" },
      { key: "followers_lost", label: "Followers lost" },
      { key: "followers_net", label: "Net followers" },
      { key: "fans_current", label: "Fans now" },
      { key: "fans_added", label: "Fans gained" },
      { key: "fans_lost", label: "Fans lost" },
      { key: "fans_net", label: "Net fans" },
      { key: "bookmarks_current", label: "Bookmarks now" },
      { key: "bookmarks_added", label: "Bookmarks added" },
      { key: "bookmarks_removed", label: "Bookmarks removed" },
      { key: "bookmarks_net", label: "Net bookmarks" },
    ],
  },
  {
    id: "interest",
    title: "Questions & meeting requests",
    description: "Counts and metadata only — never question, answer or message content. "
      + "A pitch-deck submission is the primary conversion for a Capital profile.",
    metrics: [
      { key: "questions", label: "Questions received" },
      { key: "questions_answered", label: "Questions answered" },
      { key: "meeting_requests", label: "Meeting requests" },
      { key: "unique_converters", label: "Unique converters" },
      { key: "conversion_rate", label: "Conversion rate", suffix: "%" },
      { key: "conversations_started", label: "Conversations started" },
      { key: "unique_conversation_starters", label: "Unique conversation starters" },
    ],
  },
  {
    id: "actions",
    title: "Outbound & internal actions",
    description: "Aggregate click counts only. The endpoint reports one total for each, so no "
      + "per-link or per-destination split is shown, and no address, URL, handle, destination id or "
      + "visitor identity is recorded or displayed.",
    metrics: [
      { key: "outbound_clicks", label: "Outbound link clicks" },
      { key: "internal_clicks", label: "Internal link clicks" },
    ],
  },
  {
    id: "engagement",
    title: "Chatter & engagement",
    metrics: [
      { key: "engagement_actions", label: "Engagement actions" },
      { key: "unique_engagers", label: "Unique engagers" },
      { key: "comments", label: "Comments" },
      { key: "reactions", label: "Reactions" },
      { key: "rechats", label: "Re-chats" },
    ],
  },
];

/** Pitch-deck breakdown dimensions, keyed exactly as the server returns them. */
export const SUBMISSION_DIMENSION_LABEL = {
  submission_industry: "Industry",
  submission_stage: "Stage",
  submission_classification: "Classification",
  submission_country: "Country",
  submission_region: "Region",
  submission_source: "Submission source",
};
